import { trpc, trpcApi } from '@bitbot/api';
import { useProfile } from '../user';
import { useToken } from '../user/token';
import { debounce } from 'lodash';
import { toastError } from '@bitbot/ui-design';
import { useState } from 'react';

export const useReferral = () => {
  const { hasLogin } = useToken();
  const { data: referralInfo } = trpc.referral.getReferralState.useQuery(
    undefined,
    {
      enabled: hasLogin,
    },
  );
  return {
    referralInfo,
  };
};

export interface IActivity {
  id: number;
  type: string;
  title: string;
  description: string;
  logo: string;
  link: string;
  point: number;
  linkType: string;
  finished: boolean;
}

export const usePoints = () => {
  const { hasLogin } = useToken();
  const [currentActivity, setCurrentActivity] = useState<IActivity | null>(
    null,
  );
  const { data: rewardInfo, refetch: refetchRewardInfo } =
    trpc.reward.getRewardTask.useQuery(undefined, {
      enabled: hasLogin,
    });
  return {
    rewardInfo: (rewardInfo?.rewardTasks ?? []) as IActivity[],
    refetchRewardInfo,
    currentActivity,
    setCurrentActivity,
  };
};

export const useCheckOnceReward = () => {
  const { refetchRewardInfo } = usePoints();
  const { refetchUserInfo } = useProfile();
  const [isChecking, setIsChecking] = useState(false);
  const checkReward = async (rewardType: string) => {
    if (isChecking) return;
    setIsChecking(true);
    try {
      const data = await trpcApi.reward.checkOnceReward.query({
        rewardType,
      });
      if (data?.success) {
        refetchRewardInfo();
        refetchUserInfo();
      }
      return data;
    } catch (error) {
      return { success: false };
    } finally {
      setIsChecking(false);
    }
  };
  return {
    checkReward,
  };
};

export const useSetOnceReward = (rewardType: string) => {
  const { refetchRewardInfo } = usePoints();
  const { refetchUserInfo } = useProfile();
  const setRewardMutation = trpc.reward.setOnceReward.useMutation();
  const setOnceReward = debounce(async () => {
    try {
      await setRewardMutation.mutateAsync({
        rewardType,
      });
      refetchRewardInfo();
      refetchUserInfo();
    } catch (error: any) {
      toastError(error.message);
    }
  }, 300);
  return {
    setOnceReward,
  };
};
